<template>
  <div class="root">
    <img src="/images/topImage.png" class="full-width" />
    <v-layout column wrap align-center class="container">
      <h1 class="main-title">
        {{ $t("title.search") }}
      </h1>
      <div class="field-container">
        <div class="subheading">
          {{ $t("label.select_a_direction") }}
        </div>
        <v-select
          v-model="direction"
          :items="directionItems"
          solo
          hide-details
          :menu-props="{ contentClass: 'select-content-multi-line' }"
          @change="onChangeDirection"
        />
      </div>
      <div class="field-container">
        <div class="subheading">
          {{ $t("label.boarding_date") }}
        </div>
        <v-select
          v-model="date"
          :items="dateItems"
          solo
          hide-details
          @change="onChangeDate"
        />
      </div>
      <div class="field-container">
        <div class="subheading">
          {{ $t("label.name_of_train") }}
        </div>
        <v-select
          v-model="train"
          :items="trainItems"
          :loading="loadingSchedules"
          :disabled="!direction"
          solo
          hide-details
          :menu-props="{ contentClass: 'select-content-multi-line' }"
          :no-data-text="$t('message.no_data')"
          @change="onChangeTrain"
        />
      </div>
      <div class="field-container">
        <div class="subheading">
          {{ $t("label.select_a_sheet_class") }}
        </div>
        <v-select
          v-model="sheet"
          :items="sheetItems"
          :loading="loadingSchedules"
          :disabled="!train"
          solo
          hide-details
          @change="saveParams"
        />
      </div>
      <div class="btn-search-container">
        <v-btn
          color="primary"
          class="btn-search"
          :disabled="!(direction && date && train && sheet)"
          @click="onClickSearch"
        >
          {{ $t("label.search") }}
        </v-btn>
      </div>
    </v-layout>
    <error-dialog ref="errorDialog" />
  </div>
</template>

<script>
import dateFormat from "dateformat";
import BaseComponent from "@/components/BaseComponent";
import ErrorDialog from "@/components/ErrorDialog";
import Api from "@/lib/Api";

// TRAINS_SCHEDULEテーブルのDestStationカラム
const DIR_OBIHIRO_KUSHIRO = "帯広,釧路";
const DIR_SAPPORO = "札幌";

export default {
  name: "Search",
  extends: BaseComponent,
  components: {
    ErrorDialog,
  },
  data() {
    return {
      direction: null,
      date: null,
      train: null,
      sheet: null,
      loadingSchedules: false,
      schedules: [],
      directionItems: this.createDirectionItems(),
      dateItems: this.createDateItems(),
      trainItems: [],
      sheetItems: [],
    };
  },
  created() {
    if (this.isDistanceValidated()) {
      this.restoreParams();
      this.reloadTrainItems();
    } else {
      this.$router.push({ name: "Caution" });
    }
  },
  methods: {
    createDirectionItems() {
      return [
        {
          text: this.$t("direction.for_sapporo"),
          value: DIR_SAPPORO,
        },
        {
          text: this.$t("direction.for_obihiro_kushiro"),
          value: DIR_OBIHIRO_KUSHIRO,
        },
      ];
    },
    createDateItems() {
      return ["today", "tomorrow"].map((val) => {
        return {
          text: this.$t(`boarding_date.${val}`),
          value: val,
        };
      });
    },
    saveParams() {
      this.$storage.set("search_params", {
        direction: this.direction,
        date: this.date,
        train: this.train,
        sheet: this.sheet,
      });
    },
    restoreParams() {
      let params = this.$storage.get("search_params", {
        direction: null,
        date: "today",
        train: null,
        sheet: "non_reserved",
      });
      this.direction = params.direction;
      this.date = params.date;
      this.train = params.train;
      this.sheet = params.sheet;
    },
    onChangeDirection() {
      this.train = null;
      this.saveParams();
      this.reloadTrainItems();
    },
    onChangeDate() {
      this.train = null;
      this.saveParams();
      this.reloadTrainItems();
    },
    onChangeTrain() {
      this.saveParams();
      this.updateSheetItems();
    },
    // 運行スケジュール検索 -> 「列車」項目を更新
    reloadTrainItems() {
      if (!this.direction) {
        return;
      }
      if (!this.date) {
        return;
      }

      this.loadingSchedules = true;
      this.searchSchedules()
        .then((schedules) => {
          this.schedules = schedules;
          this.updateTrainItems();
          this.updateSheetItems();
        })
        .catch((error) => {
          window.console.log(error);
          this.$refs.errorDialog.open(
            "error_message.api_failed_to_get_train_schedule"
          );
        })
        .then(() => {
          this.loadingSchedules = false;
        });
    },
    searchSchedules() {
      let params = {
        station_code: "K22",
        dest_stations: this.direction,
        date: this.getIsoDate(),
      };
      if (this.date === "today") {
        params["since_time"] = dateFormat(new Date(), "HH:MM");
      }
      return Api.searchSchedules(params);
    },
    getIsoDate() {
      let date = new Date();
      if (this.date === "tomorrow") {
        date.setDate(date.getDate() + 1);
      }
      return dateFormat(date, "isoDate");
    },
    updateTrainItems() {
      this.trainItems = this.schedules.map((item) => {
        let i18nName = this.vehicleTypeToI18nName(item);
        let i18nParams = {
          no: item.TrainNumber,
          time: item.TrainTime,
        };
        return {
          text: this.$t(i18nName, i18nParams),
          value: item.KeyID,
        };
      });
      this.train = this.trainItems.length > 0 ? this.trainItems[0].value : null;
    },
    // VehicleType -> i18n設定名
    vehicleTypeToI18nName(schedule) {
      switch (schedule.TrainName) {
        //case '261':
        case "とかち":
          return "train.super_tokachi";
        //case '283':
        case "おおぞら":
          return "train.super_aozora";
        default:
          return "train.unknown";
      }
    },
    // 「指定席・自由席」項目を更新
    updateSheetItems() {
      let items = [
        { text: this.$t("sheet.non_reserved"), value: "non_reserved" },
      ];
      this.createReservedSheetItems().forEach((item) => items.push(item));
      items.push({ text: this.$t("sheet.no_ticket"), value: "no_ticket" });
      this.sheetItems = items;
    },
    createReservedSheetItems() {
      let schedule = this.findSelectedSchedule();
      if (!schedule) {
        return [];
      }

      let items = [];
      for (let no = 1; no <= 10; no++) {
        let vehicleType = schedule[`Vehicle${no}VehicleType`];
        switch (vehicleType) {
          case "R":
            items.push(this.createReservedSheetItem("sheet.reserved", no));
            break;
          case "G":
            items.push(this.createReservedSheetItem("sheet.green", no));
            break;
          case "N":
          case "O":
          case "P":
            items.push(this.createReservedSheetItem2("sheet.reserved", no));
            break;
        }
      }
      return items;
    },
    createReservedSheetItem(i18nName, no) {
      return {
        text: this.$t(i18nName, { no }),
        value: no,
      };
    },
    createReservedSheetItem2(i18nName, no) {
      let value_no = no;
      no = no - 1;
      return {
        text: this.$t(i18nName, { no }),
        value: value_no,
      };
    },
    onClickSearch() {
      let s = this.findSelectedSchedule();
      if (!s) {
        return;
      }

      let dayOffset = this.date === "today" ? "00" : "01";
      let no =
        this.sheet === "non_reserved" || this.sheet === "no_ticket"
          ? 0
          : this.sheet;
      let fileName = `${s.UseHome}_${s.TrainNo}_guideimage_${dayOffset}_${no}.png`;

      this.$router.push({
        name: "ImageView",
        params: { fileName },
      });
    },
    findSelectedSchedule() {
      if (!this.schedules) {
        return null;
      }
      if (!this.train) {
        return null;
      }
      return this.schedules.find((s) => s.KeyID === this.train);
    },
  },
};
</script>

<style lang="scss" scoped>
.root {
  padding-top: 16px;
}
.full-width {
  display: block;
  width: 100%;
}
.container {
  padding: 16px;
  padding-top: 0;
  align-items: stretch;
}
.main-title {
  color: white;
  text-align: center;
  text-shadow: 2px 2px 3px black;
}
.field-container {
  padding-top: 16px;
  .subheading {
    color: white;
    font-weight: bold;
  }
}
.btn-search-container {
  padding-top: 16px;
  text-align: center;
  .btn-search {
    font-size: 18px;
    text-transform: none;
  }
}
</style>

<style lang="scss">
/* 英語の場合に札幌方面の表示と列車名が長いので折り返す */
.select-content-multi-line {
  [role="listitem"] {
    .v-list__tile__title {
      height: 100%;
      white-space: normal;
      display: flex;
      align-items: center;
    }
  }
}
</style>
